import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Button, Stack, Typography } from '@mui/material';

import { Modal } from 'components/Modal';
import { FormProvider, TextField } from 'components/Form';
import useYupValidationResolver from 'hook/useYupValidationResolver';

import { useCreateLevelStructure } from '../api/createLevelStructure';
import { useUpdateLevelStructure } from '../api/updateLevelStructure';
import { levelStructureValidationSchema, newLevelStructureValidationSchema } from '../validation';

export const LevelStructureFormModal = ({
  resource,
  id,
  structure,
  open,
  handleClose,
  onSaved,
}) => {
  const isEdit = !!structure?.level_structure_id;

  const methods = useForm({
    defaultValues: { name: '', lowest_level_name: '', lowest_level_description: '' },
    resolver: useYupValidationResolver(
      isEdit ? levelStructureValidationSchema : newLevelStructureValidationSchema
    ),
  });

  const onSuccess = () => {
    onSaved();
    handleClose();
  };
  const { mutateAsync: createLevelStructure } = useCreateLevelStructure(resource, id, onSuccess);
  const { mutateAsync: updateLevelStructure } = useUpdateLevelStructure(resource, id, onSuccess);

  const { reset } = methods;
  useEffect(() => {
    if (open) {
      reset({ name: structure?.name ?? '', lowest_level_name: '', lowest_level_description: '' });
    }
  }, [open, structure, reset]);

  const onSubmit = ({ name, lowest_level_name, lowest_level_description }) => {
    const request = isEdit
      ? updateLevelStructure({ structureId: structure.level_structure_id, name })
      : createLevelStructure({
          name,
          lowest_level: { name: lowest_level_name, description: lowest_level_description },
        });
    request.catch(() => {});
  };

  return (
    <Modal
      title={isEdit ? 'Edit level structure' : 'Add level structure'}
      open={open}
      handleClose={handleClose}
    >
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Stack gap={3} mb={3}>
          <TextField fullWidth name="name" label="Name" placeholder="Please enter name" required />
          {!isEdit && (
            <>
              <Typography variant="body2" color="text.secondary">
                A level structure needs at least one level. Add the lowest one now, and the rest
                after saving.
              </Typography>
              <TextField
                fullWidth
                name="lowest_level_name"
                label="Lowest level name"
                placeholder="Please enter level name"
                required
              />
              <TextField
                fullWidth
                name="lowest_level_description"
                label="Lowest level description"
                placeholder="Please enter level description"
                multiline
                minRows={3}
                sx={{ '& .MuiInputBase-root': { p: 0 } }}
              />
            </>
          )}
        </Stack>

        <Stack direction="row" gap={3} justifyContent="center">
          <Button variant="outlined" color="primary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" type="submit">
            {isEdit ? 'Update' : 'Add'}
          </Button>
        </Stack>
      </FormProvider>
    </Modal>
  );
};
